/**
 * Phase 11 Beat 1 — Candidate Portal "My Dossier" tab (UAT Feb 2026 rev-2).
 *
 * Talks to:
 *   GET  /api/portal/dossier            — return current dossier + submitted_at
 *   PATCH /api/portal/dossier           — save draft (merge-patch)
 *   POST /api/portal/dossier/submit     — finalize submission (once)
 *
 * UAT enhancements over rev-1:
 *   1. Phone → PhoneField with fixed +1 country code, validation, 10-digit US format.
 *   2. Address → split into Street / City (searchable) / State (dropdown) / Zip / Country.
 *   3. Education → Degree + Institution dropdowns w/ "Other" free-text; Passing Month+Year selects.
 *   4. Skills → searchable multi-select with custom-add support.
 *   5. Visa → Visa Type (dropdown) + Start/End dates, removed Work Authorization.
 *   6. Work Experience → Role dropdown w/ "Other", native date pickers, Present toggle.
 *   7. Visual polish → gradient header, progress meter, sticky footer bar, roomy spacing.
 *
 * `authFetch` and `me` come from the parent CandidatePortal shell.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  FloppyDisk, PaperPlaneTilt, Plus, Trash, GraduationCap, Briefcase, IdentificationBadge,
  MapPin, Sparkle, User, Buildings, ChartBar, ShieldCheck,
} from "@phosphor-icons/react";

import SearchableSelect from "@/components/portal/SearchableSelect";
import MultiSelectWithCustom from "@/components/portal/MultiSelectWithCustom";
import PhoneField, { isValidUSPhone } from "@/components/portal/PhoneField";

import { US_STATE_OPTIONS } from "@/data/us-states";
import { US_CITIES } from "@/data/us-cities";
import { DEGREES } from "@/data/degrees";
import { ROLES } from "@/data/roles";
import { VISA_TYPES } from "@/data/visa-types";
import { SKILLS } from "@/data/skills";

const OTHER_VALUE = "Other";
const MONTHS = [
  { value: "01", label: "January" }, { value: "02", label: "February" }, { value: "03", label: "March" },
  { value: "04", label: "April" }, { value: "05", label: "May" }, { value: "06", label: "June" },
  { value: "07", label: "July" }, { value: "08", label: "August" }, { value: "09", label: "September" },
  { value: "10", label: "October" }, { value: "11", label: "November" }, { value: "12", label: "December" },
];
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: CURRENT_YEAR + 6 - 1965 }, (_, i) => String(CURRENT_YEAR + 5 - i));
const COUNTRIES = [
  "United States", "Canada", "Mexico", "United Kingdom", "India",
  "Australia", "Germany", "France", "Spain", "Italy", "Netherlands",
  "Ireland", "Singapore", "Japan", "China", "Brazil", "Other",
];

export default function PortalDossier({ authFetch, me, onSaved }) {
  const [dossier, setDossier] = useState(null);
  const [submittedAt, setSubmittedAt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmSubmit, setConfirmSubmit] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await authFetch("/api/portal/dossier");
      const d = await r.json();
      setDossier(d.dossier || {});
      setSubmittedAt(d.submitted_at || null);
    } catch (ex) { toast.error(ex.message); }
  }, [authFetch]);
  useEffect(() => { load(); }, [load]);

  const isSubmitted = !!submittedAt;
  const disabled = isSubmitted || busy;

  const setSection = (key) => (patch) =>
    setDossier((d) => ({ ...(d || {}), [key]: { ...((d || {})[key] || {}), ...patch } }));

  const setPersonal = setSection("personal_info");
  const setVisa = setSection("visa_info");

  const putRoot = (patch) => setDossier((d) => ({ ...(d || {}), ...patch }));

  const patchSave = async (payload, toastMsg = "Draft saved") => {
    setBusy(true);
    try {
      const r = await authFetch("/api/portal/dossier", {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error((await r.json()).detail || "Save failed");
      const d = await r.json();
      setDossier(d.dossier || {});
      toast.success(toastMsg);
      onSaved && onSaved();
    } catch (ex) { toast.error(ex.message); }
    finally { setBusy(false); }
  };

  const saveDraft = () => patchSave(dossier || {});

  const submit = async () => {
    setBusy(true); setConfirmSubmit(false);
    try {
      await authFetch("/api/portal/dossier", {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(dossier || {}),
      });
      const r = await authFetch("/api/portal/dossier/submit", { method: "POST" });
      if (!r.ok) throw new Error((await r.json()).detail || "Submit failed");
      const d = await r.json();
      setSubmittedAt(d.submitted_at);
      toast.success("Dossier submitted! Your Group Lead will now start the evaluation.");
      onSaved && onSaved();
    } catch (ex) { toast.error(ex.message); }
    finally { setBusy(false); }
  };

  const personal = dossier?.personal_info || {};
  const visa = dossier?.visa_info || {};
  const education = dossier?.education || [];
  const experience = dossier?.work_experience || [];
  const skillList = useMemo(() => dossier?.skills || [], [dossier?.skills]);

  const addRow = (key, empty) => setDossier((d) => ({ ...(d || {}), [key]: [...((d || {})[key] || []), empty] }));
  const removeRow = (key, idx) => setDossier((d) => ({ ...(d || {}), [key]: ((d || {})[key] || []).filter((_, i) => i !== idx) }));
  const updateRow = (key, idx, patchRow) => setDossier((d) => {
    const rows = [...((d || {})[key] || [])];
    rows[idx] = { ...rows[idx], ...patchRow };
    return { ...(d || {}), [key]: rows };
  });

  // Phone field: read legacy `phone` string if new split fields absent.
  const phoneValue = useMemo(() => {
    if (personal.phone_number || personal.phone_country_code) {
      return {
        country_code: personal.phone_country_code || "+1",
        national_number: personal.phone_number || "",
      };
    }
    return { country_code: "+1", national_number: personal.phone || "" };
  }, [personal.phone_number, personal.phone_country_code, personal.phone]);

  // Completeness meter — motivational nudge.
  const progress = useMemo(() => {
    const p = dossier?.personal_info || {};
    const v = dossier?.visa_info || {};
    const edu = dossier?.education || [];
    const exp = dossier?.work_experience || [];
    const sk = dossier?.skills || [];
    const checks = [
      !!p.first_name, !!p.last_name,
      !!(p.phone_number || p.phone),
      !!p.street_address, !!p.current_city, !!p.current_state,
      !!p.zip_code, !!(p.current_country || "United States"),
      edu.length > 0, exp.length > 0, sk.length > 0,
      !!(v.visa_type || v.visa_status),
      !!(dossier?.linkedin_url), !!(dossier?.salary_expectation), !!(dossier?.notice_period),
    ];
    const filled = checks.filter(Boolean).length;
    return Math.round((filled / checks.length) * 100);
  }, [dossier]);

  const phoneValid = !phoneValue.national_number || isValidUSPhone(phoneValue.national_number);

  if (dossier === null) return (
    <div className="min-h-[400px] flex items-center justify-center">
      <div className="text-slate-400 text-sm inline-flex items-center gap-2">
        <div className="w-4 h-4 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
        Loading your dossier…
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="portal-dossier">
      {/* Header banner */}
      <div className="rounded-2xl overflow-hidden border border-slate-200 bg-gradient-to-br from-sky-600 via-sky-700 to-indigo-800 text-white shadow-sm">
        <div className="px-6 py-5 flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-sky-200 mb-1.5">Candidate Dossier</div>
            <h2 className="text-2xl md:text-3xl font-semibold leading-tight">
              {isSubmitted
                ? <>Submitted for evaluation <span className="text-sky-200">✓</span></>
                : <>Complete your profile</>}
            </h2>
            <p className="text-sm text-sky-100/90 mt-1 max-w-lg">
              {isSubmitted
                ? "Your Group Lead will review this information and start the evaluation shortly. Contact them to change anything."
                : "Fill in the sections below. Save drafts anytime — nothing is submitted until you press the Submit button."}
            </p>
          </div>
          <div className="text-right shrink-0">
            <div className="text-4xl font-bold tabular-nums" data-testid="dos-progress-pct">{progress}%</div>
            <div className="text-xs text-sky-200 uppercase tracking-widest mt-0.5">Complete</div>
          </div>
        </div>
        <div className="h-2 bg-sky-900/40">
          <div className="h-full bg-white/90 transition-all duration-500 ease-out" style={{ width: `${progress}%` }} data-testid="dos-progress-bar" />
        </div>
      </div>

      {isSubmitted && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-900 px-4 py-3 text-sm flex items-start gap-2" data-testid="dossier-submitted-banner">
          <ShieldCheck weight="fill" size={18} className="text-emerald-600 shrink-0 mt-0.5" />
          <div>
            <div className="font-medium">Submitted on {submittedAt?.slice(0, 10)}</div>
            <div className="text-xs text-emerald-800 mt-0.5">Your Group Lead will start the evaluation shortly. Contact them to update any information after submission.</div>
          </div>
        </div>
      )}

      {/* Personal Information */}
      <Section icon={User} title="Personal Information" subtitle="Basic contact details">
        <Grid>
          <Field label="First Name" required>
            <TextInput value={personal.first_name || ""} disabled={disabled}
                       onChange={(v) => setPersonal({ first_name: v })} testid="dos-first" placeholder="Jane" />
          </Field>
          <Field label="Last Name" required>
            <TextInput value={personal.last_name || ""} disabled={disabled}
                       onChange={(v) => setPersonal({ last_name: v })} testid="dos-last" placeholder="Doe" />
          </Field>
          <Field label="Phone" required>
            <PhoneField value={phoneValue} disabled={disabled}
                        testid="dos-phone"
                        onChange={(v) => setPersonal({
                          phone_country_code: v.country_code,
                          phone_number: v.national_number,
                          phone: v.national_number ? `${v.country_code} ${v.national_number}` : "",
                        })} />
          </Field>
          <Field label="Date of Birth">
            <TextInput type="date" value={personal.date_of_birth || ""} disabled={disabled}
                       onChange={(v) => setPersonal({ date_of_birth: v })} testid="dos-dob" />
          </Field>
        </Grid>
      </Section>

      {/* Current Address */}
      <Section icon={MapPin} title="Current Address" subtitle="Your primary US mailing address">
        <div className="space-y-3">
          <Field label="Street / Suite / House Number" required>
            <TextInput value={personal.street_address || ""} disabled={disabled}
                       onChange={(v) => setPersonal({ street_address: v })} testid="dos-street"
                       placeholder="123 Main Street, Apt 4B" />
          </Field>
          <Grid>
            <Field label="City" required>
              <SearchableSelect
                options={US_CITIES}
                value={personal.current_city || ""}
                onChange={(v) => setPersonal({ current_city: v })}
                disabled={disabled} allowCustom
                placeholder="Search city…"
                testid="dos-city"
              />
            </Field>
            <Field label="State" required>
              <SearchableSelect
                options={US_STATE_OPTIONS}
                value={personal.current_state || ""}
                onChange={(v) => setPersonal({ current_state: v })}
                disabled={disabled}
                placeholder="Select state…"
                testid="dos-state"
              />
            </Field>
            <Field label="Zip Code" required>
              <TextInput
                value={personal.zip_code || ""} disabled={disabled}
                onChange={(v) => setPersonal({ zip_code: v.replace(/[^0-9-]/g, "").slice(0, 10) })}
                testid="dos-zip" placeholder="10001" inputMode="numeric"
              />
            </Field>
            <Field label="Country">
              <SearchableSelect
                options={COUNTRIES}
                value={personal.current_country || "United States"}
                onChange={(v) => setPersonal({ current_country: v })}
                disabled={disabled} allowCustom
                placeholder="United States"
                testid="dos-country"
              />
            </Field>
          </Grid>
          <div className="flex flex-wrap gap-6 pt-2 border-t border-slate-100">
            <ToggleField label="Willing to Relocate" value={!!personal.willing_to_relocate} disabled={disabled}
                         onChange={(v) => setPersonal({ willing_to_relocate: v })} testid="dos-relocate" />
            <ToggleField label="Open to Remote Work" value={!!personal.remote_ok} disabled={disabled}
                         onChange={(v) => setPersonal({ remote_ok: v })} testid="dos-remote" />
          </div>
        </div>
      </Section>

      {/* Education */}
      <Section icon={GraduationCap} title="Education" subtitle="List your degrees, most recent first">
        {education.length === 0 && (
          <EmptyHint text="No education added yet. Add your most recent degree first." />
        )}
        {education.map((r, i) => (
          <EducationCard key={`ed-${i}`} row={r} idx={i} disabled={disabled}
                         onUpdate={(p) => updateRow("education", i, p)}
                         onRemove={() => removeRow("education", i)} />
        ))}
        {!disabled && (
          <button onClick={() => addRow("education", { degree: "", institution: "", passing_month: "", passing_year: "", gpa: "" })}
                  data-testid="dos-add-edu"
                  className="mt-2 text-sm border-2 border-dashed border-slate-300 rounded-lg px-4 py-2.5 hover:border-sky-400 hover:bg-sky-50/50 hover:text-sky-700 inline-flex items-center gap-2 text-slate-600 transition">
            <Plus size={14} /> Add education
          </button>
        )}
      </Section>

      {/* Work Experience */}
      <Section icon={Briefcase} title="Work Experience" subtitle="Include internships and part-time roles">
        {experience.length === 0 && (
          <EmptyHint text="No work experience added yet." />
        )}
        {experience.map((r, i) => (
          <WorkExperienceCard key={`we-${i}`} row={r} idx={i} disabled={disabled}
                              onUpdate={(p) => updateRow("work_experience", i, p)}
                              onRemove={() => removeRow("work_experience", i)} />
        ))}
        {!disabled && (
          <button onClick={() => addRow("work_experience", { company: "", role: "", start_date: "", end_date: "", description: "" })}
                  data-testid="dos-add-we"
                  className="mt-2 text-sm border-2 border-dashed border-slate-300 rounded-lg px-4 py-2.5 hover:border-sky-400 hover:bg-sky-50/50 hover:text-sky-700 inline-flex items-center gap-2 text-slate-600 transition">
            <Plus size={14} /> Add work experience
          </button>
        )}
      </Section>

      {/* Skills */}
      <Section icon={Sparkle} title="Skills" subtitle="Search or add custom skills — the more the better">
        <MultiSelectWithCustom
          options={SKILLS}
          value={skillList}
          onChange={(next) => putRoot({ skills: next })}
          disabled={disabled}
          placeholder="Search Python, Excel, AWS…"
          emptyMessage="Search or type to add skills"
          testid="dos-skills"
        />
        <p className="text-xs text-slate-500 mt-2">
          Selected <span className="font-medium text-slate-700">{skillList.length}</span> skill{skillList.length === 1 ? "" : "s"}.
          Press <kbd className="text-[10px] bg-slate-100 border border-slate-200 rounded px-1 py-0.5">Enter</kbd> to add a custom skill.
        </p>
      </Section>

      {/* Visa Information */}
      <Section icon={IdentificationBadge} title="Visa & Sponsorship" subtitle="US work-authorization details">
        <Grid>
          <Field label="Visa Type" required>
            <SearchableSelect
              options={VISA_TYPES}
              value={visa.visa_type || visa.visa_status || ""}
              onChange={(v) => setVisa({ visa_type: v, visa_status: v })}
              disabled={disabled}
              placeholder="Select visa type…"
              testid="dos-visa-type"
            />
          </Field>
          {(visa.visa_type === OTHER_VALUE) && (
            <Field label="Please specify visa type" required>
              <TextInput value={visa.visa_type_other || ""} disabled={disabled}
                         onChange={(v) => setVisa({ visa_type_other: v })} testid="dos-visa-type-other"
                         placeholder="e.g. E-2 Investor" />
            </Field>
          )}
          <Field label="Visa Start Date">
            <TextInput type="date" value={visa.visa_start_date || ""} disabled={disabled}
                       onChange={(v) => setVisa({ visa_start_date: v })} testid="dos-visa-start" />
          </Field>
          <Field label="Visa End Date">
            <TextInput type="date" value={visa.visa_end_date || ""} disabled={disabled}
                       onChange={(v) => setVisa({ visa_end_date: v })} testid="dos-visa-end" />
          </Field>
          <div className="md:col-span-2">
            <ToggleField label="Sponsorship Required" value={!!visa.sponsorship_required} disabled={disabled}
                         onChange={(v) => setVisa({ sponsorship_required: v })} testid="dos-visa-sponsor" />
          </div>
        </Grid>
      </Section>

      {/* Career Preferences */}
      <Section icon={ChartBar} title="Career Preferences" subtitle="How employers can reach you & what you're looking for">
        <Grid>
          <Field label="LinkedIn URL">
            <TextInput value={dossier?.linkedin_url || ""} disabled={disabled}
                       onChange={(v) => putRoot({ linkedin_url: v })} testid="dos-linkedin"
                       placeholder="https://linkedin.com/in/…" />
          </Field>
          <Field label="Portfolio / GitHub URL">
            <TextInput value={dossier?.portfolio_url || ""} disabled={disabled}
                       onChange={(v) => putRoot({ portfolio_url: v })} testid="dos-portfolio"
                       placeholder="https://…" />
          </Field>
          <Field label="Salary Expectation">
            <TextInput value={dossier?.salary_expectation || ""} disabled={disabled}
                       onChange={(v) => putRoot({ salary_expectation: v })} testid="dos-salary"
                       placeholder="e.g. $120k – $140k" />
          </Field>
          <Field label="Notice Period">
            <TextInput value={dossier?.notice_period || ""} disabled={disabled}
                       onChange={(v) => putRoot({ notice_period: v })} testid="dos-notice"
                       placeholder="e.g. 2 weeks" />
          </Field>
        </Grid>
      </Section>

      {/* Sticky action bar */}
      {!isSubmitted && (
        <div className="sticky bottom-0 -mx-4 md:mx-0 z-10">
          <div className="bg-white/90 backdrop-blur-md border-t border-slate-200 md:border md:rounded-2xl shadow-lg px-4 md:px-6 py-3 flex items-center gap-3 flex-wrap">
            <div className="flex-1 min-w-[180px]">
              <div className="text-xs text-slate-500">Profile completeness</div>
              <div className="text-sm font-medium text-slate-900">{progress}% complete</div>
            </div>
            <button onClick={saveDraft} disabled={busy || !phoneValid}
                    data-testid="dos-save-draft"
                    className="text-sm border border-slate-200 rounded-lg px-4 py-2 inline-flex items-center gap-1.5 hover:bg-slate-50 hover:border-slate-300 disabled:opacity-50 transition font-medium text-slate-700">
              <FloppyDisk size={14} /> Save Draft
            </button>
            <button onClick={() => setConfirmSubmit(true)} disabled={busy || !phoneValid || progress < 40}
                    data-testid="dos-submit"
                    title={progress < 40 ? "Fill at least 40% before submitting" : ""}
                    className="text-sm bg-sky-600 hover:bg-sky-700 text-white rounded-lg px-4 py-2 inline-flex items-center gap-1.5 disabled:opacity-50 transition font-medium shadow-sm">
              <PaperPlaneTilt size={14} /> Submit for Evaluation
            </button>
          </div>
        </div>
      )}

      {confirmSubmit && (
        <SubmitConfirmModal
          onCancel={() => setConfirmSubmit(false)}
          onConfirm={submit}
          progress={progress}
        />
      )}
    </div>
  );
}

/* ---------------------------- sub-components ---------------------------- */

function Section({ icon: Icon, title, subtitle, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm">
      <div className="px-5 md:px-6 pt-5 pb-3 flex items-start gap-3 border-b border-slate-100">
        {Icon && (
          <div className="w-9 h-9 rounded-lg bg-sky-50 border border-sky-100 flex items-center justify-center text-sky-600 shrink-0">
            <Icon size={18} weight="regular" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h3 className="text-sm md:text-base font-semibold text-slate-900 leading-tight">{title}</h3>
          {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      <div className="px-5 md:px-6 py-5 space-y-3">{children}</div>
    </div>
  );
}

function Grid({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>;
}

function Field({ label, required, children, hint }) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-slate-600 mb-1.5 flex items-center gap-1">
        <span>{label}</span>
        {required && <span className="text-red-500" aria-label="required">*</span>}
      </div>
      {children}
      {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
    </label>
  );
}

function TextInput({ value, onChange, disabled, testid, type = "text", placeholder, inputMode }) {
  return (
    <input
      type={type}
      value={value || ""}
      disabled={disabled}
      inputMode={inputMode}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      placeholder={placeholder}
      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white
                 focus:outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-sky-500
                 hover:border-slate-300 transition disabled:bg-slate-50 disabled:text-slate-500"
    />
  );
}

function ToggleField({ label, value, onChange, disabled, testid }) {
  return (
    <label className="inline-flex items-center gap-3 cursor-pointer select-none">
      <button
        type="button"
        role="switch"
        aria-checked={value}
        disabled={disabled}
        onClick={() => onChange(!value)}
        data-testid={testid}
        className={`relative w-11 h-6 rounded-full transition-colors duration-200 ${value ? "bg-sky-600" : "bg-slate-300"} ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200 ${value ? "translate-x-5" : ""}`}
        />
      </button>
      <span className="text-sm text-slate-700 font-medium">{label}</span>
    </label>
  );
}

function EmptyHint({ text }) {
  return <div className="text-xs text-slate-400 italic px-3 py-2 bg-slate-50/60 rounded-md border border-dashed border-slate-200">{text}</div>;
}

function EducationCard({ row, idx, disabled, onUpdate, onRemove }) {
  const isDegreeOther = row.degree === OTHER_VALUE;
  return (
    <div className="border border-slate-200 rounded-xl p-4 mb-3 bg-slate-50/40" data-testid={`dos-edu-${idx}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-wider font-medium text-slate-500 inline-flex items-center gap-1.5">
          <Buildings size={12} /> Education #{idx + 1}
        </div>
        {!disabled && (
          <button onClick={onRemove} className="text-xs text-red-500 hover:text-red-700 inline-flex items-center gap-1 font-medium"
                  data-testid={`dos-edu-remove-${idx}`}>
            <Trash size={12} /> Remove
          </button>
        )}
      </div>
      <Grid>
        <Field label="Degree" required>
          <SearchableSelect
            options={DEGREES}
            value={row.degree || ""}
            onChange={(v) => onUpdate({ degree: v, ...(v !== OTHER_VALUE ? { degree_other: "" } : {}) })}
            disabled={disabled}
            placeholder="Select degree…"
            testid={`dos-edu-degree-${idx}`}
          />
        </Field>
        {isDegreeOther && (
          <Field label="Please specify degree" required>
            <TextInput value={row.degree_other || ""} disabled={disabled}
                       onChange={(v) => onUpdate({ degree_other: v })}
                       testid={`dos-edu-degree-other-${idx}`}
                       placeholder="e.g. B.Sc (Statistics)" />
          </Field>
        )}
        <Field label="Institution" required>
          <TextInput value={row.institution || ""} disabled={disabled}
                     onChange={(v) => onUpdate({ institution: v })}
                     testid={`dos-edu-inst-${idx}`}
                     placeholder="University of Texas at Austin" />
        </Field>
        <Field label="Passing Month">
          <SearchableSelect
            options={MONTHS}
            value={row.passing_month || ""}
            onChange={(v) => onUpdate({ passing_month: v })}
            disabled={disabled}
            placeholder="Month…"
            testid={`dos-edu-month-${idx}`}
          />
        </Field>
        <Field label="Passing Year">
          <SearchableSelect
            options={YEARS}
            value={row.passing_year || row.year || ""}
            onChange={(v) => onUpdate({ passing_year: v, year: v })}
            disabled={disabled}
            placeholder="Year…"
            testid={`dos-edu-year-${idx}`}
          />
        </Field>
        <Field label="GPA">
          <TextInput value={row.gpa || ""} disabled={disabled}
                     onChange={(v) => onUpdate({ gpa: v })}
                     testid={`dos-edu-gpa-${idx}`}
                     placeholder="3.8 / 4.0" />
        </Field>
      </Grid>
    </div>
  );
}

function WorkExperienceCard({ row, idx, disabled, onUpdate, onRemove }) {
  const isRoleOther = row.role === OTHER_VALUE;
  const isPresent = !row.end_date;
  return (
    <div className="border border-slate-200 rounded-xl p-4 mb-3 bg-slate-50/40" data-testid={`dos-we-${idx}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-wider font-medium text-slate-500 inline-flex items-center gap-1.5">
          <Briefcase size={12} /> Experience #{idx + 1}
        </div>
        {!disabled && (
          <button onClick={onRemove} className="text-xs text-red-500 hover:text-red-700 inline-flex items-center gap-1 font-medium"
                  data-testid={`dos-we-remove-${idx}`}>
            <Trash size={12} /> Remove
          </button>
        )}
      </div>
      <Grid>
        <Field label="Company" required>
          <TextInput value={row.company || ""} disabled={disabled}
                     onChange={(v) => onUpdate({ company: v })}
                     testid={`dos-we-company-${idx}`}
                     placeholder="Acme Corp." />
        </Field>
        <Field label="Role" required>
          <SearchableSelect
            options={ROLES}
            value={row.role || row.title || ""}
            onChange={(v) => onUpdate({ role: v, title: v, ...(v !== OTHER_VALUE ? { role_other: "" } : {}) })}
            disabled={disabled}
            placeholder="Select role…"
            testid={`dos-we-role-${idx}`}
          />
        </Field>
        {isRoleOther && (
          <Field label="Please specify role" required>
            <TextInput value={row.role_other || ""} disabled={disabled}
                       onChange={(v) => onUpdate({ role_other: v, title: v })}
                       testid={`dos-we-role-other-${idx}`}
                       placeholder="e.g. Head of Growth" />
          </Field>
        )}
        <Field label="Start Date" required>
          <TextInput type="date" value={row.start_date || row.start || ""} disabled={disabled}
                     onChange={(v) => onUpdate({ start_date: v, start: v })}
                     testid={`dos-we-start-${idx}`} />
        </Field>
        <Field label="End Date" hint={isPresent ? "Currently working here" : ""}>
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <TextInput type="date" value={row.end_date || ""} disabled={disabled || isPresent}
                         onChange={(v) => onUpdate({ end_date: v, end: v })}
                         testid={`dos-we-end-${idx}`} />
            </div>
            {!disabled && (
              <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer whitespace-nowrap">
                <input type="checkbox" checked={isPresent}
                       onChange={(e) => onUpdate({ end_date: e.target.checked ? "" : new Date().toISOString().slice(0, 10), end: e.target.checked ? "Present" : "" })}
                       data-testid={`dos-we-present-${idx}`}
                       className="rounded border-slate-300 text-sky-600 focus:ring-sky-500" />
                Present
              </label>
            )}
          </div>
        </Field>
        <div className="md:col-span-2">
          <Field label="Description">
            <textarea rows={3} value={row.description || ""}
                      disabled={disabled}
                      onChange={(e) => onUpdate({ description: e.target.value })}
                      data-testid={`dos-we-desc-${idx}`}
                      placeholder="Impact, achievements, technologies used…"
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white
                                 focus:outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-sky-500
                                 hover:border-slate-300 transition disabled:bg-slate-50 resize-none" />
          </Field>
        </div>
      </Grid>
    </div>
  );
}

function SubmitConfirmModal({ onCancel, onConfirm, progress }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4" data-testid="dos-confirm-modal">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-50 border border-amber-200 text-amber-600 flex items-center justify-center">
            <PaperPlaneTilt size={20} />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-slate-900">Submit dossier for evaluation?</h3>
            <p className="text-sm text-slate-600 mt-1">
              Once submitted, you won't be able to edit your dossier. Your Group Lead will start the evaluation.
            </p>
            <p className="text-xs text-slate-500 mt-2">Currently at {progress}% completion — you can always ask your Group Lead to update details after submission.</p>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
          <button onClick={onCancel} className="text-sm px-4 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 font-medium text-slate-700"
                  data-testid="dos-confirm-cancel">
            Continue editing
          </button>
          <button onClick={onConfirm} className="text-sm px-4 py-2 rounded-lg bg-sky-600 hover:bg-sky-700 text-white font-medium inline-flex items-center gap-1.5"
                  data-testid="dos-confirm-submit">
            <PaperPlaneTilt size={14} /> Yes, Submit
          </button>
        </div>
      </div>
    </div>
  );
}
